import { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useUpdateVendor, useVendor } from '../../../hooks/useVendors';
import { useMyPermissions } from '../../../hooks/useMyPermissions';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage, apiErrorStatus } from '../../../utils/apiError';
import {
    MAX_VENDOR_NAME_LENGTH,
    type OrderMethodDetail,
    type UpdateVendorBody,
    type VendorDetail
} from '../../../services/vendors';
import { Drawer, DrawerSection, KVRow } from '../Drawer';
import { StatusBadge, type Tone } from '../StatusBadge';
import { ModalField, MODAL_INPUT_CLASSES } from '../../shared/ModalField';
import { OrderMethodModal } from './OrderMethodModal';
import { OrderMethodsSection } from './OrderMethodsSection';
import { useVendorEditForm } from './useVendorEditForm';
import { toRollup, vendorDispatchBlock, type VendorBlock } from './vendorDispatch';

const VENDOR_STATE_TONES: Record<string, Tone> = {
    active: { bg: 'bg-emerald-600' },
    retired: { bg: 'bg-gray-400', strike: true }
};

const NOTICE_COPY: Record<VendorBlock, { title: string; detail: string; danger: boolean }> = {
    retired: {
        title: 'Retired vendor',
        detail: 'Retired vendors are skipped by ordering. Existing products and purchase orders keep referencing it.',
        danger: false
    },
    no_methods: {
        title: 'Cannot be ordered from',
        detail: 'This vendor has no ordering methods, so its products cannot be assigned one. Add a method below.',
        danger: true
    },
    no_usable_method: {
        title: 'Cannot be ordered from',
        detail: 'No ordering method is usable, so automatic ordering silently does nothing. Fix a method below.',
        danger: true
    }
};

function DispatchNotice({ vendor }: { vendor: VendorDetail }) {
    const block = vendorDispatchBlock(toRollup(vendor));
    if (block === null) return null;
    const copy = NOTICE_COPY[block];
    return (
        <div
            className={`flex items-start gap-2.5 p-3 rounded-lg border ${
                copy.danger
                    ? 'border-red-300 bg-red-50 text-red-900'
                    : 'border-amber-300 bg-amber-50 text-amber-900'
            }`}
        >
            <ExclamationTriangleIcon
                className={`h-4 w-4 shrink-0 mt-0.5 ${
                    copy.danger ? 'text-red-700' : 'text-amber-700'
                }`}
            />
            <div className="min-w-0">
                <p className="text-sm font-semibold">{copy.title}</p>
                <p className="text-xs mt-0.5">{copy.detail}</p>
            </div>
        </div>
    );
}

interface VendorDrawerProps {
    vendorId: string;
    onClose: () => void;
}

export function VendorDrawer({ vendorId, onClose }: VendorDrawerProps) {
    const { vendor, isLoading, isError, error, refetch } = useVendor(vendorId);
    const { hasPermission } = useMyPermissions();
    const canEdit = hasPermission('billing.update');
    const { showToast } = useToast();
    const { mutateAsync: updateVendor, isPending: isSaving } = useUpdateVendor();
    const [methodTarget, setMethodTarget] = useState<OrderMethodDetail | 'new' | null>(null);
    const { name, setName, nameValid, isDirty, changes } = useVendorEditForm(vendor);

    const save = async (id: string, changes: UpdateVendorBody) => {
        try {
            const result = await updateVendor({ id, body: changes });
            showToast('Vendor saved', 'success');
            // A legitimate save that breaks dispatch still says so.
            if (result.warning) showToast(result.warning.message, 'error');
        } catch (err) {
            showToast(apiErrorMessage(err, 'Failed to save the vendor'), 'error');
        }
    };

    if (isLoading) {
        return (
            <Drawer title="Vendor" onClose={onClose}>
                <div className="flex items-center justify-center gap-2 text-gray-400 py-16">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                    <span className="text-sm">Loading vendor…</span>
                </div>
            </Drawer>
        );
    }

    // A 500 or a dropped connection must not claim the vendor doesn't exist.
    if (isError || !vendor) {
        const isMissing = apiErrorStatus(error) === 404;
        return (
            <Drawer title="Vendor" onClose={onClose}>
                {isMissing ? (
                    <p className="text-sm text-gray-500 py-16 text-center">Vendor not found.</p>
                ) : (
                    <div className="flex flex-col items-center gap-3 py-16 text-center">
                        <p className="text-sm text-gray-600">
                            {apiErrorMessage(error, "Couldn't load this vendor.")}
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                void refetch();
                            }}
                            className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                        >
                            Retry
                        </button>
                    </div>
                )}
            </Drawer>
        );
    }

    return (
        <>
            <Drawer
                title={vendor.name}
                badge={
                    <StatusBadge
                        status={vendor.isActive ? 'active' : 'retired'}
                        toneOverride={VENDOR_STATE_TONES}
                    />
                }
                subtitle={vendor.id}
                onClose={onClose}
                closeDisabled={isSaving}
                footer={
                    canEdit && (
                        <div className="flex items-center justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    void save(vendor.id, changes);
                                }}
                                disabled={!isDirty || !nameValid || isSaving}
                                className="bg-black text-white font-semibold px-5 py-2 rounded-lg text-sm hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSaving ? 'Saving…' : 'Save changes'}
                            </button>
                        </div>
                    )
                }
            >
                <DrawerSection title="Settings">
                    <ModalField htmlFor="vendor-name" label="Name" required>
                        <input
                            id="vendor-name"
                            type="text"
                            value={name}
                            disabled={!canEdit || isSaving}
                            maxLength={MAX_VENDOR_NAME_LENGTH}
                            onChange={(e) => setName(e.target.value)}
                            className={MODAL_INPUT_CLASSES}
                        />
                    </ModalField>
                </DrawerSection>

                <DispatchNotice vendor={vendor} />

                <DrawerSection title="Usage">
                    <div className="flex flex-col">
                        <KVRow k="Slug">
                            <span className="font-mono text-[12px]">{vendor.id}</span>
                        </KVRow>
                        <KVRow k="Products">{vendor.references.linkedProducts}</KVRow>
                        <KVRow k="Live purchase orders">
                            {vendor.references.livePurchaseOrders}
                        </KVRow>
                    </div>
                </DrawerSection>

                <OrderMethodsSection
                    methods={vendor.orderMethods}
                    canEdit={canEdit}
                    onAdd={() => setMethodTarget('new')}
                    onEdit={setMethodTarget}
                />

                {!canEdit && (
                    <p className="text-xs text-gray-400 pb-2">
                        Read-only: you don't have billing edit access.
                    </p>
                )}
            </Drawer>

            {methodTarget && (
                <OrderMethodModal
                    vendorId={vendor.id}
                    method={methodTarget === 'new' ? undefined : methodTarget}
                    onClose={() => setMethodTarget(null)}
                />
            )}
        </>
    );
}
